import { TAppDispatch, TGetState } from 'store/types'
import slice from 'store/features/project/slice'
import {
  getProject_API,
  createProject_API,
  renderProject_API,
  patchProject_API,
  getProjectMetaData_API,
  deleteProjectThumbnail_API,
  uploadProjectThumbnail_API
} from 'api'
import {
  compressImageIfNeeded,
  generateThumbnailFromMediaItem,
  calculateFrameIndex
} from 'utils/thumbnail'
import { IMediaItem } from 'types/media'
import { goToWatchPage } from 'utils'
import { projectSelectors } from 'store/features/project/selectors'
import { ErrorKeys } from 'consts/errorKeys'
import { ApiError } from 'utils/errors'
import { handlePlanLimitError } from 'utils/limitErrors'
import * as Sentry from '@sentry/react'
import { handleApiError } from 'store/utils'
import { userSelectors } from '../user/selectors'
import {
  TUserPlan,
  USER_PLAN_TO_MAX_DURATION,
  USER_PLAN_TO_QUALITIES
} from 'consts/user'
import { withAppThunks } from 'store/with-app-thunks'
import { isAllowedDurationSelector } from 'store/selectors'
import { RENDER_PROJECT_ERROR_CODE, upgradePopupErrorCodes } from 'api/errors'
import { NEW_PROJECT_NAME } from 'consts'

const { actions } = slice

const getProject = (id: string) => async (dispatch: TAppDispatch) => {
  try {
    dispatch(actions.setIsLoading(true))
    const response = await getProject_API(id)
    const projectMetaDataId = response.data.meta?.genAiProjectId ?? null
    if (projectMetaDataId) {
      const projectMetaDataResponse = await getProjectMetaData_API(
        String(projectMetaDataId)
      )

      dispatch(actions.setMetaData(projectMetaDataResponse.data))
    }
    dispatch(actions.setProject(response.data))
    Sentry.setTag('projectId', id)
  } catch (error) {
    const { status } = (error as ApiError).cause || {}

    if (status === 404) {
      dispatch(handleApiError(ErrorKeys.NOT_FOUND_PROJECT))
    }
  } finally {
    dispatch(actions.setIsLoading(false))
  }
}

const createProject =
  (name: string) =>
  async (dispatch: TAppDispatch): Promise<number> => {
    try {
      const urlSearchParams = new URLSearchParams(window.location.search)
      const referrer = urlSearchParams.get('referrer')
      dispatch(actions.setIsLoading(true))
      const response = await createProject_API({
        name,
        ...(referrer && {
          meta: {
            referrer
          }
        })
      })
      dispatch(actions.setCreatedProject(response.data.project))

      return response.data.project.id
    } catch (error) {
      // Plan cap on video projects (HTTP 409 + meta.draftLimit). Surfaced
      // here — the single funnel for all create entry points — as the upgrade
      // popup, whose headline names the limit; then rethrown, and callers'
      // catches recognize it via isDraftLimitError and skip their generic
      // popups.
      handlePlanLimitError(error)
      throw error
    } finally {
      dispatch(actions.setIsLoading(false))
    }
  }

const renderProject = ({
  projectId,
  quality,
  uid
}: {
  projectId: string
  quality: number
  uid: string
}) =>
  withAppThunks(
    appThunks => async (dispatch: TAppDispatch, getState: TGetState) => {
      try {
        const state = getState()
        const subscription = userSelectors.userCurrentPlanId(state)
        const isAllowedDuration = isAllowedDurationSelector(state)

        USER_PLAN_TO_MAX_DURATION[subscription as TUserPlan]
        const allowedQuality = USER_PLAN_TO_QUALITIES[subscription as TUserPlan]
        const isAllowedQuality = allowedQuality.includes(quality)
        const shouldUpgrade = !isAllowedQuality || !isAllowedDuration
        if (shouldUpgrade) {
          dispatch(appThunks.editorThunks.openUpgradePopup('upgrade-popup'))
          return
        }

        dispatch(actions.setIsRendering(true))
        await renderProject_API(projectId, quality)
        goToWatchPage(uid, quality)
      } catch (error) {
        const apiError = error as ApiError
        const errorCode = apiError.errorCode as RENDER_PROJECT_ERROR_CODE

        if (upgradePopupErrorCodes.includes(errorCode)) {
          dispatch(appThunks.editorThunks.openUpgradePopup('upgrade-popup'))
        } else {
          dispatch(
            handleApiError(
              ErrorKeys.GENERAL_API_ERROR,
              apiError?.message ?? error
            )
          )
        }
        dispatch(actions.setIsRendering(false))
      }
    }
  )

const createProjectIfNotExists =
  (name: string) =>
  async (
    dispatch: TAppDispatch,
    getState: TGetState
  ): Promise<number | undefined> => {
    const state = getState()
    const projectData = projectSelectors.data(state)
    if (Object.keys(projectData).length === 0) {
      const projectId = await dispatch(createProject(name))
      return projectId
    }
  }

const getOrCreateProjectId =
  () =>
  async (dispatch: TAppDispatch, getState: TGetState): Promise<number> => {
    const state = getState()
    const projectId = projectSelectors.id(state)
    const newProjectId = await dispatch(
      createProjectIfNotExists(NEW_PROJECT_NAME)
    )

    return Number(newProjectId || projectId)
  }

const updateProjectName =
  (name: string) => async (dispatch: TAppDispatch, getState: TGetState) => {
    const projectId = projectSelectors.id(getState())

    try {
      await patchProject_API(`${projectId}`, { name })
      dispatch(actions.setProjectDataName(name))
    } catch (error) {
      dispatch(
        handleApiError(ErrorKeys.CUSTOM_API_ERROR, (error as ApiError).message)
      )
    }
  }

const deleteProjectThumbnail =
  () => async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const projectId = projectSelectors.id(state)

    if (!projectId) {
      return
    }

    try {
      await deleteProjectThumbnail_API(`${projectId}`)
      dispatch(actions.setProjectThumbnailPath(null))
    } catch (error) {
      throw new Error('Error deleting thumbnail', { cause: error })
    }
  }

const generateAndUploadProjectThumbnail =
  (mediaItem: IMediaItem) =>
  async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const projectId = projectSelectors.id(state)

    if (!projectId) {
      return
    }

    try {
      const thumbnailFile = await generateThumbnailFromMediaItem(mediaItem)

      if (!thumbnailFile) {
        return
      }

      const compressedThumbnail = await compressImageIfNeeded(thumbnailFile)
      const formData = new FormData()
      formData.append('thumbnail', compressedThumbnail)

      await uploadProjectThumbnail_API(`${projectId}`, formData)

      const path = mediaItem.meta.previewPath || mediaItem.meta.path || ''
      const frameIndex = calculateFrameIndex(mediaItem)

      dispatch(
        actions.setProjectThumbnailPath({
          id: mediaItem.id,
          path,
          frameIndex
        })
      )
    } catch (error) {
      throw new Error('Error generating thumbnail', { cause: error })
    }
  }

export const projectThunks = {
  ...actions,
  getProject,
  createProject,
  renderProject,
  createProjectIfNotExists,
  updateProjectName,
  getOrCreateProjectId,
  deleteProjectThumbnail,
  generateAndUploadProjectThumbnail
}
